import {
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Select,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useRef, useState } from 'react';

const DOCUMENT_TYPES = [
  { value: 'cc', label: 'Cédula de Ciudadanía' },
  { value: 'ce', label: 'Cédula de Extranjería' },
  { value: 'nit', label: 'NIT' },
] as const;

type DocumentType = (typeof DOCUMENT_TYPES)[number]['value'];

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (input: {
    puntosColombiaDocumentType: DocumentType;
    puntosColombiaDocumentNumber: string;
  }) => Promise<void>;
};

const PuntosColombiaRetryModal = ({ isOpen, onClose, onConfirm }: Props): JSX.Element => {
  const [documentType, setDocumentType] = useState<DocumentType | ''>('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [hasVerifiedAccount, setHasVerifiedAccount] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const initialFocusRef = useRef<HTMLSelectElement>(null);

  const trimmedNumber = documentNumber.trim();
  const isNumberValid = /^\d{5,20}$/.test(trimmedNumber);
  const showNumberError = !!trimmedNumber && !isNumberValid;
  const canSubmit = !!documentType && isNumberValid && hasVerifiedAccount;

  const handleClose = () => {
    onClose();
    setDocumentType('');
    setDocumentNumber('');
    setHasVerifiedAccount(false);
  };

  const handleConfirm = async () => {
    if (!documentType || !isNumberValid) return;
    setIsSubmitting(true);
    try {
      await onConfirm({
        puntosColombiaDocumentType: documentType,
        puntosColombiaDocumentNumber: trimmedNumber,
      });
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered initialFocusRef={initialFocusRef}>
      <ModalOverlay />
      <ModalContent mx={4}>
        <ModalHeader fontSize="lg" fontWeight="bold">
          Reintentar acreditación
        </ModalHeader>
        <ModalBody>
          <Stack spacing={4}>
            <FormControl>
              <FormLabel fontSize="0.875rem">Tipo de documento</FormLabel>
              <Select
                ref={initialFocusRef}
                placeholder="Seleccionar"
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value as DocumentType)}
              >
                {DOCUMENT_TYPES.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl isInvalid={showNumberError}>
              <FormLabel fontSize="0.875rem">Número de documento</FormLabel>
              <Input
                placeholder="Número de documento"
                value={documentNumber}
                inputMode="numeric"
                onChange={(e) => setDocumentNumber(e.target.value)}
              />
              <FormErrorMessage>Debe tener entre 5 y 20 dígitos.</FormErrorMessage>
            </FormControl>
            <Checkbox
              colorScheme="purple"
              alignItems="flex-start"
              isChecked={hasVerifiedAccount}
              onChange={(e) => setHasVerifiedAccount(e.target.checked)}
            >
              <Text fontSize="0.8rem" lineHeight={1.4} mt="-2px">
                Ya verifiqué que tengo una cuenta activa en Puntos Colombia con este documento
              </Text>
            </Checkbox>
          </Stack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={handleClose}>
            Volver
          </Button>
          <Button colorScheme="purple" onClick={handleConfirm} isDisabled={!canSubmit} isLoading={isSubmitting}>
            Reintentar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PuntosColombiaRetryModal;
